import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { throwError, catchError, switchMap, timer } from 'rxjs';
import { retryWhen, take, delayWhen } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { AlertService } from '../service/alert.service';

function retryHttpRequestAfterRefresh(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    buildHeaders: (token?: string | null) => Record<string, string>,
    authService: AuthService
) {
    const newToken = authService.getToken();
    const retryReq = req.clone({
        setHeaders: buildHeaders(newToken),
    });
    return next(retryReq);
}

function handleTokenRefreshFailure(authService: AuthService, router: Router, alertService: AlertService, error: HttpErrorResponse) {
    authService.removeToken();
    authService.removeVendorData();
    authService.removeTenantData();

    const hasApiMessage = typeof error.error?.message === 'string' && error.error.message.trim();
    const title = hasApiMessage ? 'Error' : 'Session Expired';
    const message = hasApiMessage ? error.error.message : 'Your session has expired. Please log in again.';
    const type = hasApiMessage ? 'error' : 'warning';

    alertService.showAlert(title, message, type);

    if (!router.url.includes('/login')) {
        router.navigate(['/login']);
    }

    return throwError(() => error);
}

function handle401Unauthorized(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    router: Router,
    alertService: AlertService,
    error: HttpErrorResponse,
    buildHeaders: (token?: string | null) => Record<string, string>
) {
    return authService.refreshTokenIfNeeded().pipe(
        switchMap((refreshSuccessful) => {
            if (refreshSuccessful) {
                return retryHttpRequestAfterRefresh(req, next, buildHeaders, authService);
            }
            return handleTokenRefreshFailure(authService, router, alertService, error);
        }),
        catchError(() => handleTokenRefreshFailure(authService, router, alertService, error))
    );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const alertService = inject(AlertService);
    const token = authService.getToken();
    const lang = localStorage.getItem('language') || 'en';

    // Safely get tenant data
    let getTenant = '';
    try {
        const tenantData = authService.getTenantDataValue();
        getTenant = (tenantData && typeof tenantData === 'object' && tenantData.tenant_subdomain) || '';
    } catch (error) {
        getTenant = '';
    }

    // Skip token refresh attempts for certain paths
    const skipTokenRefresh = req.url.includes('/auth/admin/login') || req.url.includes('/auth/refresh-token') || req.url.includes('/auth/forgot-password');
    const isChangePasswordRequest = req.url.includes('/auth/change-password');

    // Skip tenant header for certain paths
    const skipTenantHeader =
        req.url.includes('/login') ||
        // req.url.includes('/profile') ||
        //  req.url.includes('/auth/') ||
        req.url.includes('/forgot-password');

    // Helper function to build headers
    const buildHeaders = (token?: string | null): Record<string, string> => {
        const headers: Record<string, string> = {};

        if (!req.headers.has('x-custom-lang')) {
            headers['x-custom-lang'] = lang;
        }

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        if (!skipTenantHeader && !req.headers.has('x-tenant-id')) {
            // Only add tenant header if tenant ID is available and the request did not set one
            if (getTenant && getTenant !== 'select_tenant') {
                headers['x-tenant-id'] = getTenant;
            }
        }

        return headers;
    };

    // Note: Removed proactive token refresh - let session service handle timing
    // Only refresh tokens reactively when server returns 401 errors

    // Clone the request to add the Authorization header
    const authReq = req.clone({
        setHeaders: buildHeaders(token),
    });

    return next(authReq).pipe(
        // Retry mechanism with exponential backoff for server errors
        // Skip retry for refresh-token endpoint - if it fails, we should logout immediately
        retryWhen((errors) =>
            errors.pipe(
                delayWhen((error: HttpErrorResponse, index: number) => {
                    // Don't retry refresh-token endpoint errors - logout immediately
                    if (req.url.includes('/auth/refresh-token') || req.url.includes('update-password')) {
                        return throwError(() => error);
                    }
                    // Only retry on specific server errors (500, 502, 503, 504)
                    if ([500, 502, 503, 504].includes(error.status) && index < 3) {
                        // Exponential backoff: 1s, 2s, 4s
                        return timer(Math.pow(2, index) * 1000);
                    }
                    return throwError(() => error);
                }),
                take(3) // Maximum 3 retries
            )
        ),
        catchError((error: HttpErrorResponse) => {
            // Handle refresh-token endpoint errors - logout and redirect
            const hasApiMessage = typeof error.error?.message === 'string' && error.error.message.trim();

            const title = hasApiMessage ? 'Error' : 'Session Expired';

            const message = hasApiMessage ? error.error.message : 'Your session has expired. Please log in again.';

            const type = hasApiMessage ? 'error' : 'warning';
            const isCategoryMasterRequest = req.url.includes('/category');
            if (isCategoryMasterRequest) {
                return throwError(() => error);
            }
            alertService.showAlert(title, message, type);

            // Check for 401 Unauthorized errors
            if (error.status === 401 && !skipTokenRefresh && !isChangePasswordRequest) {
                return handle401Unauthorized(req, next, authService, router, alertService, error, buildHeaders);
            }

            // Handle network errors during server downtime
            if (error.status === 0 || error.status === 504) {
                alertService.showAlert('Connection Error', 'Server is temporarily unavailable. Please try again in a few moments.', 'warning');
            }

            // 401 on login is handled in LoginComponent (shows API message e.g. inactive account)
            const isAdminLoginRequest = req.url.includes('/auth/admin/login');
            if (error.status === 401 && req.url.includes('/auth/') && !isChangePasswordRequest && !isAdminLoginRequest) {
                const msg =
                    typeof error.error?.message === 'string' && error.error.message.trim()
                        ? error.error.message
                        : 'Please check your credentials and try again.';
                alertService.showAlert('Authentication Error', msg, 'error');
            }

            // For other errors or if token refresh is skipped, just pass through the error
            return throwError(() => error);
        })
    );
};
